import { SafeImage } from '~/components/safe-image'
import { DialogLink } from '~/components/dialog-link'
import { PlusIcon } from '~/components/icons'
import { SidewaysRow } from '~/components/ui/sideways-row'
import { useApi } from '~/data/data-context'
import { photoName, todayKey, type WeighInPhoto } from '~/data/weighins'

/**
 * A day's progress photos, in the weigh-in they belong to: the day's shots in a row that
 * scrolls, pressable to full screen, removable one by one, and the tile that adds the next one
 * at the end of them.
 *
 * They were a panel of their own once, opened from a second row in the account menu. But a
 * weigh-in is one act — you stand on the scale, you take the tape round, you take the photo —
 * and splitting it in two made somebody choose which half they were about to do before they
 * had done either.
 */
export function WeighInPhotoStrip({
  day,
  photos,
  onShow,
  onRemove,
}: {
  /** The day being shown, as `YYYY-MM-DD`. */
  day: string
  photos: WeighInPhoto[]
  onShow: (photo: WeighInPhoto) => void
  onRemove: (photo: WeighInPhoto) => void
}) {
  const api = useApi()
  return (
    <div>
      <p className="pb-2 text-xs tracking-wide text-ink-muted uppercase">Photos</p>
      {/*
        Sideways, with the way to add one at the end. A day's shots are few, and a grid of them
        would push the numbers above off the card on the days there are several — this way the
        row is the same height whether the day holds none or six.
      */}
      <SidewaysRow as="ul" className="gap-2">
        {photos.map((photo) => (
          <li key={photo.id} className="w-24 shrink-0">
            <button
              type="button"
              className="block w-full overflow-hidden rounded-xl bg-surface"
              onClick={() => {
                onShow(photo)
              }}
            >
              <SafeImage
                src={api.weighInPhotoUrl(photo.id)}
                alt={photoName(photo)}
                className="aspect-[3/4] w-full object-cover"
              />
            </button>
            <div className="mt-0.5 flex items-center gap-1">
              <p className="min-w-0 flex-1 truncate text-xs text-ink-muted">{photoName(photo)}</p>
              <button
                type="button"
                aria-label={`Remove ${photoName(photo)} photo`}
                className="shrink-0 rounded-full px-1.5 text-xs text-ink-muted transition-colors hover:bg-hover hover:text-ink"
                onClick={() => {
                  onRemove(photo)
                }}
              >
                ✕
              </button>
            </div>
          </li>
        ))}
        <li className="w-24 shrink-0">
          <DialogLink
            dialog="weighin-add-photo"
            {...(day === todayKey() ? {} : { day })}
            label="Add a photo"
            className="flex aspect-[3/4] w-full flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-white/15 text-ink-muted transition-colors hover:bg-hover hover:text-ink"
          >
            <PlusIcon aria-hidden className="size-7" />
            <span className="text-xs">Add</span>
          </DialogLink>
        </li>
      </SidewaysRow>
    </div>
  )
}
